import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import { GST_RATE, computeGst, formatMoney } from '../../../utils/tax';
import { isValidGstin, normalizeGstin } from '../../../utils/gstin';

const PRESET_AMOUNTS = [500, 1000, 2000, 5000];

const GST_PERCENT_LABEL = `${Math.round(GST_RATE * 100)}%`;

const formatCurrency = (amount) => {
  if (amount == null) return '0';
  return Number(amount).toLocaleString('en-IN');
};

const AddFundsModal = ({ onClose, onSuccess, defaultAmount = 0, gstOnFile = true }) => {
  const prefill = Math.ceil(Number(defaultAmount));
  const hasDefault = Number.isFinite(prefill) && prefill > 0;
  const [amount, setAmount] = useState(hasDefault ? 'custom' : null);
  const [customAmount, setCustomAmount] = useState(hasDefault ? String(prefill) : '');
  const [gstNumber, setGstNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const parsedCustom = parseFloat(customAmount);
  const selectedAmount = amount === 'custom'
    ? (Number.isFinite(parsedCustom) ? parsedCustom : 0)
    : (amount || 0);
  const isValidAmount = selectedAmount >= 1;
  const { base: baseAmount, gstAmount, total: totalAmount } = computeGst(isValidAmount ? selectedAmount : 0);

  const showGstField = !gstOnFile;
  const gstEntered = gstNumber.trim().length > 0;
  const gstInvalid = gstEntered && !isValidGstin(gstNumber);
  const canProceed = isValidAmount && !gstInvalid;

  const handleProceed = async () => {
    if (!canProceed) return;
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/api/v1/wallet/add-funds', {
        amount: baseAmount,
        expectedGstAmount: gstAmount,
        expectedTotal: totalAmount,
        ...(showGstField && gstEntered && !gstInvalid ? { gstNumber: normalizeGstin(gstNumber) } : {}),
      });
      const data = res.data?.data;
      const paymentSessionId = data?.paymentSessionId;
      const walletOrderId = data?.orderId;

      if (paymentSessionId) {
        // Save order info to localStorage so we can check status on return.
        // Prefer server-computed tax values; fall back to locally computed ones
        // until the backend returns them.
        localStorage.setItem('wallet_pending_order', JSON.stringify({
          orderId: walletOrderId,
          amount: data?.baseAmount ?? baseAmount,
          gstAmount: data?.gstAmount ?? gstAmount,
          totalAmount: data?.totalAmount ?? totalAmount,
        }));

        // Redirect current page to Cashfree full-page checkout
        // eslint-disable-next-line no-undef
        const cashfree = Cashfree({ mode: 'production' });
        cashfree.checkout({
          paymentSessionId: paymentSessionId,
          redirectTarget: '_self',
        });
      } else {
        setError('Payment session not received. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Add funds failed:', err);
      const msg = err?.response?.data?.message || 'Failed to initiate payment. Please try again.';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Funds</h2>
            <p className="text-sm text-gray-500 mt-0.5">Select an amount to add to your wallet.</p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
            <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
              <Icon name="X" size={12} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-5">
          {/* Preset amounts */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => { setAmount(preset); setCustomAmount(''); }}
                className={`flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-xl border text-sm font-semibold transition-all ${
                  amount === preset
                    ? 'border-[#2F55FF] bg-blue-50/60 text-[#2F55FF] shadow-sm'
                    : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                ₹{formatCurrency(preset)}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">₹</span>
            <input
              type="number"
              placeholder="Enter custom amount"
              value={customAmount}
              min="1"
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setAmount('custom');
              }}
              onFocus={() => setAmount('custom')}
              className={`w-full pl-8 pr-3 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors placeholder-gray-400 ${
                amount === 'custom' ? 'border-[#2F55FF] bg-blue-50/30' : 'border-gray-200'
              }`}
            />
          </div>

          {/* GST breakdown */}
          {isValidAmount && (
            <div className="mt-4 px-3.5 py-3 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium text-gray-800">₹{formatMoney(baseAmount)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-600 mt-1.5">
                <span>GST ({GST_PERCENT_LABEL})</span>
                <span className="font-medium text-gray-800">₹{formatMoney(gstAmount)}</span>
              </div>
              <div className="border-t border-gray-200 mt-2.5 pt-2.5 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Total payable</span>
                <span className="text-sm font-bold text-gray-900">₹{formatMoney(totalAmount)}</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">
                ₹{formatMoney(baseAmount)} will be credited to your wallet.
              </p>
            </div>
          )}

          {/* GST number (optional, only when none on file) */}
          {showGstField && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                GST Number <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="22AAAAA0000A1Z5"
                value={gstNumber}
                maxLength={15}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-colors tracking-wide ${
                  gstInvalid
                    ? 'border-red-300 focus:ring-red-500/20 focus:border-red-400'
                    : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-400'
                }`}
              />
              <p className={`text-[11px] mt-1 ${gstInvalid ? 'text-red-600' : 'text-gray-500'}`}>
                {gstInvalid
                  ? 'Enter a valid 15-character GSTIN or leave blank.'
                  : ''}
              </p>
            </div>
          )}

          {/* Info */}
          <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
            <Icon name="Info" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              Amounts are exclusive of {GST_PERCENT_LABEL} GST. You'll be redirected to a secure payment page and return here automatically after payment.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleProceed}
            disabled={!canProceed || loading}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl transition-colors shadow-sm ${
              canProceed && !loading
                ? 'bg-[#2F55FF] text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Redirecting...
              </>
            ) : (
              <>
                <Icon name="CreditCard" size={15} />
                Pay ₹{isValidAmount ? formatMoney(totalAmount) : '0'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFundsModal;
